/*
  predictive-search.js: the header search pop-up.

  2026-10-20 search round 3 (shipped 2026-10-21 with Paul's yes). Spec P0-7, plus the pop-up side of
  P0-3 (Enter), P0-6 (the health line) and P0-8 (Help rows). Source: research/spec-final.md, 20 Oct 2026.

  Why: the old pop-up waited 500 ms, asked Shopify for loose products ("pyrite" showed Sodalite) and ran
  the Liquid router on every request, so Best Match came late and Enter could land somewhere else.
  - Best Match is worked out in the browser on every keystroke from the 3rd letter (BBQuery.parse with
    prefixLast, assets/bb-query.js), with no network wait. Its link is the same route Enter uses.
  - Shopify is asked only for query suggestions, pages and articles, 250 ms after the last keystroke
    (sections/predictive-search.liquid returns them as a hidden list).
  - Products come from the destination itself (BBQuery.peek, sections/bb-search-peek.liquid), fetched
    250 ms after the last keystroke and cached by route, so the pop-up shows what the page will show.
  - Order: Best Match, Suggestions, Products, Guides and Articles, See All Results. For questions the
    guides move up under Best Match.
  - Enter (or the phone's Search key) calls BBQuery.go: one navigation, to where Best Match points.
    If BBQuery or its word list did not load, the form submits to /search as before.
  - Arrow keys walk one flat list of every option. A highlighted suggestion is copied into the field;
    Up from the first row returns to the field with the typed words. Escape closes and keeps the text
    (assets/search-modal.js). The field is cleared only by the Clear button.
  Kept from the theme file: the custom element, the live region, the loading spinner and Clear.
  Styles: assets/bb-search-popup.css. Dependency-free, ES2017.

  2026-10-20 search round 3 fix stage (shipped 2026-10-21 with Paul's yes), from the review:
  - a row is never left on screen with a link built from earlier letters ("selenigh" -> Shop All Selenite
    stayed up after "selenight"): while the new words load, only See All Results (their own route) shows;
  - "Nothing matched" only when the destination itself is empty: a question peeks its Guides view (the
    guides come from there when Shopify suggests none), and an exact search with no named piece peeks
    "Pieces That Mention ..." like the landing does;
  - See All Results is exactly where Enter goes, the product shortcut included;
  - a stone with 2 pieces is a "Shop All" row with "2 products", not one piece's name and price;
  - the Beginner and New Home kits show under Best Match when they lead the landing;
  - on a computer See All Results stays at the bottom of the panel, and suggestions drop to 4 when
    products show, so the panel fits;
  - aria-expanded follows the list, "1 result", and the health line is announced.
*/
(function () {
  'use strict';
  if (!window.customElements || customElements.get('predictive-search')) return;

  // Customer-visible words (Title Case labels; the health and deals lines are drafts for Paul).
  const COPY = {
    best: 'Best Match',
    dym: 'Did You Mean',
    products: 'Products',
    guides: 'Guides and Articles',
    all: 'See All Results',
    shopAll: 'Shop All ',
    help: 'Help',
    guide: 'Guide',
    journal: 'Journal',
    count: '{n} products',
    from: 'From ',
    soldOut: 'Sold out',
    deals: 'Looking for a Deal?',
    health: "We don't suggest crystals for health conditions. Please talk with your doctor.",
    nothing: 'Nothing matched “{q}”. Try a stone name or a feeling, or start here.',
    results: '{n} results',
    result1: '1 result',
    t2: 'Pieces That Mention “{q}”',
    kit: 'Crystal Kit',
    none: 'No results'
  };
  const PHONE = '(max-width: 749px)';
  const DEBOUNCE = 250;
  // Routes that are not product lists: no peek, no product rows. (A question peeks its Guides view for the count and the guides.)
  const NO_PEEK = ['health', 'service', 'deals', 'category', 'product', 'unavailable'];
  // Single-concept collections read "Shop All <name>".
  const SHOP_ALL = ['stone', 'intention', 'chakra', 'zodiac', 'form', 'shape', 'gift', 'shop'];
  const SEARCH_ICON = '<svg class="bb-pp__sicon" viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false"><path fill="currentColor" d="M22.03 20.97l-5.87-5.87A8 8 0 1 0 15.1 16.16l5.87 5.87 1.06-1.06zM3.5 10a6.5 6.5 0 1 1 13 0 6.5 6.5 0 0 1-13 0z"/></svg>';
  let uidCount = 0;

  // BBQuery when it and its word list are ready, else null (the pop-up then works like a plain search box).
  function Q() {
    const b = window.BBQuery;
    try { return b && b.ready && b.ready() ? b : null; } catch (e) { return null; }
  }
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function fill(t, n) { return String(t).replace('{n}', n).replace('{q}', n); }
  function letters(s) { return String(s || '').replace(/\s+/g, '').length; }
  function norm(s) {
    const q = Q();
    if (q && q.normalize) return q.normalize(s);
    return String(s || '').toLowerCase().replace(/\s+/g, ' ').trim();
  }
  // Cache key for a term (spec P0-7: every run of spaces, not only the first).
  function termKey(s) { return String(s || '').trim().toLowerCase().replace(/\s+/g, '-'); }
  // One key per destination: q / bb_q only echo the words and Shopify's _pos/_psq/_ss tracking
  // params do not change the page, so "amet" and "amethyst" share the Amethyst peek.
  function routeKey(url) {
    const s = String(url || '').replace(/^https?:\/\/[^/]+/, '').split('#')[0];
    const i = s.indexOf('?');
    const path = (i < 0 ? s : s.slice(0, i)).replace(/\/$/, '') || '/';
    if (i < 0) return path;
    const coll = path.indexOf('/collections/') === 0;
    const keep = s.slice(i + 1).split('&').filter(function (kv) {
      if (!kv || kv.charAt(0) === '_' || kv.indexOf('bb_q=') === 0) return false;
      if (coll && kv.indexOf('q=') === 0) return false;
      return true;
    });
    return path + (keep.length ? '?' + keep.join('&') : '');
  }
  // Same photo at another width (Shopify CDN width param).
  function sized(src, w) {
    if (!src) return '';
    if (/[?&]width=\d+/.test(src)) return src.replace(/([?&])width=\d+/, '$1width=' + w);
    return src + (src.indexOf('?') >= 0 ? '&' : '?') + 'width=' + w;
  }
  // "Amethyst Raw Crystals | AA Grade | Brazil | 1-2 Inch" -> name and "AA Grade | Brazil | 1-2″".
  function splitTitle(title) {
    const parts = String(title || '').split('|').map(function (s) { return s.trim(); }).filter(Boolean);
    const spec = parts.slice(1).join(' | ').replace(/ Inches\b/g, '″').replace(/ Inch\b/g, '″');
    return { name: parts[0] || '', spec: spec };
  }
  function priceText(it) {
    if (!it.available) return COPY.soldOut;
    return (it.varies ? COPY.from : '') + (it.price || '');
  }
  function rank(list) {
    return list.slice().sort(function (a, b) { return (b.vol - a.vol) || (b.count - a.count) || (a.i - b.i); });
  }

  // Read the hidden list sections/predictive-search.liquid returns.
  function readNet(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const box = doc.querySelector('.bb-psd');
    const out = { queries: [], guides: [] };
    if (!box) return out;
    box.querySelectorAll('a[data-q]').forEach(function (a) {
      const parts = [];
      a.childNodes.forEach(function (n) {
        const t = n.textContent;
        if (!t) return;
        parts.push({ t: t, typed: n.nodeType === 1 && n.tagName === 'MARK' });
      });
      out.queries.push({ text: a.getAttribute('data-q') || a.textContent, parts: parts, url: a.getAttribute('href') });
    });
    box.querySelectorAll('a[data-g]').forEach(function (a) {
      out.guides.push({ kind: a.getAttribute('data-g'), title: a.textContent.trim(), url: a.getAttribute('href') });
    });
    return out;
  }

  // Until layout/theme.liquid loads the word list and the parser on every page, load them here once,
  // only when no script tag for them exists yet (so they are never loaded twice).
  function loadBBQuery(host) {
    try {
      if ((window.BBQuery && window.BBWords) || window.__bbQueryLoading) return;
      window.__bbQueryLoading = true;
      [['bb-query-words.js', host.getAttribute('data-bbw-src')], ['bb-query.js', host.getAttribute('data-bbq-src')]].forEach(function (f) {
        if (!f[1]) return;
        const have = Array.prototype.some.call(document.scripts, function (s) { return s.src && s.src.indexOf('/' + f[0]) >= 0; });
        if (have) return;
        const s = document.createElement('script');
        s.src = f[1];
        s.async = false;
        document.head.appendChild(s);
      });
    } catch (e) { /* the form still submits to /search */ }
  }

  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.uid = 'bbps' + (++uidCount);
      this.modal = this.closest('.search-modal');
      this.input = this.querySelector('.search__input');
      this.form = this.querySelector('form.search-modal__form');
      this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
      this.loadingSpinner = this.querySelector('.loading-overlay__spinner');
      this.statusElement = this.querySelector('.predictive-search-status');
      this.buttonText = this.querySelector('.search__button-text');
      this.buttonClass = this.getAttribute('data-button-class') || 'solid-button';
      this.cachedResults = {};   // predictive data by term key
      this.peeks = {};           // destination peeks by route key: { done, d, href }
      this.parsed = {};          // suggestion parses by text
      this.abortController = null;
      this.timer = 0;
      this.typed = '';           // the words as typed (put back after arrowing through suggestions)
      this.term = '';
      this.p = null;
      this.net = null;
      this.netKey = '';
      this.lastNet = null;
      this.activeId = '';
      this.groupEls = {};
      this.root = null;
      this.nothingEl = null;
      this.kits = {};            // kit products by handle: { done, ok, title, img, price }
      this.setupEventListeners();
      loadBBQuery(this);
      this.prefill();
    }

    setupEventListeners() {
      this.form.addEventListener('submit', this.onFormSubmit.bind(this));
      const reset = this.querySelector('button[type="reset"]');
      if (reset) reset.addEventListener('click', this.clear.bind(this));
      this.input.addEventListener('input', this.onInput.bind(this));
      this.input.addEventListener('focus', this.onFocus.bind(this));
      this.input.addEventListener('keydown', this.onKeydown.bind(this));
      this.predictiveSearchResults.addEventListener('click', this.onResultsClick.bind(this));
      // The close button, the overlay, Cancel and Escape close the modal (assets/search-modal.js);
      // the words stay in the field.
      document.addEventListener('searchmodal:close', this.close.bind(this, false));
    }

    // P0-4: on a search landing the field starts with the shopper's own words (never search syntax).
    prefill() {
      try {
        if (this.input.value) { this.typed = this.input.value; return; }
        const u = new URL(window.location.href);
        let v = u.searchParams.get('bb_q') || '';
        if (!v && u.pathname.indexOf('/collections/') >= 0) v = u.searchParams.get('q') || '';
        if (v && !/[a-z_]+:/i.test(v)) { this.input.value = v; this.typed = v; }
      } catch (e) { /* no prefill */ }
    }

    getQuery() { return this.input.value.trim(); }

    onInput() {
      this.typed = this.input.value;
      this.setActive(null);
      this.update(false);
    }

    onFocus() {
      document.body.classList.add('predictive-search--focus');
      const v = this.input.value;
      if (!v.trim()) return;
      if (this.activeEl()) return;
      if (v !== this.typed || !this.hasResults()) {
        this.typed = v;
        this.update(true);
      } else {
        this.render();
      }
    }

    // Every keystroke: Best Match now, the network 250 ms after the last keystroke.
    update(now) {
      const term = this.typed.trim();
      this.term = term;
      clearTimeout(this.timer);
      if (!term) { this.showIdle(); return; }
      const q = Q();
      try { this.p = q && letters(term) >= 3 ? q.parse(this.typed, { prefixLast: true }) : null; } catch (e) { this.p = null; }
      this.render();
      this.timer = setTimeout(this.fetchAll.bind(this), now ? 0 : DEBOUNCE);
    }

    fetchAll() {
      this.fetchPredictive(this.term);
      this.fetchPeek(this.p);
      this.fetchKit(this.p);
    }

    // 2026-10-20 fix stage: an exact search that names no piece shows "Pieces That Mention ..." on its page
    // (tier 2, assets/bb-search-landing.js); the pop-up peeks the same quoted search so it agrees.
    tier2Route(p) {
      const q = Q();
      if (!q || !p || p.noMentions || (p.kind !== 'exact' && p.kind !== 'nostock') || !p.terms || !p.terms.length) return '';
      return '/search?type=product&q=' + q.enc('"' + p.terms.join(' ').replace(/_/g, ' ') + '"');
    }

    // The kit that leads a landing ("beginner" -> Beginner Crystal Kit), read from /products/<handle>.js once.
    fetchKit(p) {
      if (!p || !p.kitLead || !p.kit || !p.kit.handle) return;
      const h = p.kit.handle;
      if (this.kits[h]) return;
      const k = this.kits[h] = { done: false, ok: false };
      fetch('/products/' + encodeURIComponent(h) + '.js', { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then((pr) => {
          if (pr && pr.available) {
            k.ok = true;
            k.title = String(pr.title || '').split('|')[0].trim();
            k.img = pr.featured_image ? (/^\/\//.test(pr.featured_image) ? 'https:' + pr.featured_image : pr.featured_image) : '';
            k.price = (pr.price_varies ? COPY.from : '') + '$' + (Math.round(pr.price) / 100).toFixed(2);
          }
        }, function () {})
        .then(() => { k.done = true; if (this.p && this.p.kit && this.p.kit.handle === h) this.render(); });
    }

    fetchPredictive(term) {
      const k = termKey(term);
      if (letters(term) < 2) { this.applyNet(k, { queries: [], guides: [] }); return; }
      if (this.cachedResults[k]) { this.applyNet(k, this.cachedResults[k]); return; }
      if (this.abortController) this.abortController.abort();
      const ac = this.abortController = new AbortController();
      this.setLoading(true);
      const url = window.routes.predictive_search_url + '?q=' + encodeURIComponent(term) +
        '&' + encodeURIComponent('resources[type]') + '=query,page,article' +
        '&' + encodeURIComponent('resources[limit]') + '=6' +
        '&' + encodeURIComponent('resources[limit_scope]') + '=each&section_id=predictive-search';
      fetch(url, { signal: ac.signal, credentials: 'same-origin' })
        .then(function (r) { if (!r.ok) throw new Error('suggest ' + r.status); return r.text(); })
        .then((html) => {
          const data = readNet(html);
          this.cachedResults[k] = data;
          if (ac === this.abortController) this.setLoading(false);
          this.applyNet(k, data);
        })
        .catch((err) => {
          if (err && err.name === 'AbortError') return;
          if (ac === this.abortController) this.setLoading(false);
          this.applyNet(k, { queries: [], guides: [], failed: true });
        });
    }

    applyNet(k, data) {
      if (!data.failed) this.lastNet = data;
      if (k === termKey(this.term)) { this.net = data; this.netKey = k; this.render(); }
    }

    peekable(p) {
      return !!(p && p.route && NO_PEEK.indexOf(p.kind) < 0 &&
        (p.route.indexOf('/collections/') === 0 || p.route.indexOf('/search?type=product') === 0 ||
          (p.kind === 'question' && p.route.indexOf('/search?') === 0)));
    }

    // The destination's first pieces and live count, cached by route (P0-3 / P0-7).
    fetchPeek(p) {
      const q = Q();
      if (!q || !this.peekable(p)) return;
      const k = routeKey(p.route);
      let needs = false;
      try { needs = q.needsCheck(p); } catch (e) { needs = false; }
      // Enter's count check reads BBQuery's own cache by the full route, so warm that entry too.
      if (needs) q.peek(p.route).catch(function () {});
      if (this.peeks[k]) { this.fetchTier2(p, this.peeks[k]); return; }
      const entry = this.peeks[k] = { done: false, d: null, href: '' };
      const after = () => {
        entry.done = true;
        this.fetchTier2(p, entry);
        if (this.p && routeKey(this.p.route) === k) this.render();
      };
      q.peek(p.route).then((d) => {
        entry.d = d;
        // A stone with one piece: Enter opens that product only when no other piece mentions the stone
        // (and never while the name is still being completed from the letters typed: BBQuery.go does the same).
        if (p.kind === 'stone' && !p.completed && p.anchor && d.count === 1 && d.items[0] && d.items[0].url) {
          const quoted = '/search?type=product&q=' + q.enc('"' + p.anchor.label.toLowerCase() + '"');
          return q.peek(quoted).then(function (qd) { if (qd.count <= 1) entry.href = d.items[0].url.split('?')[0]; }, function () {});
        }
      }).then(after, () => {
        delete this.peeks[k]; // try again on the next keystroke
        entry.failed = true;
        after();
      });
    }

    peekState(p) {
      const e = this.peeks[routeKey(p.route)];
      return e && e.done ? e : null;
    }

    // Tier 2 for an empty exact search, cached like the destination peek.
    fetchTier2(p, entry) {
      const q = Q();
      const r = this.tier2Route(p);
      if (!q || !r || !entry || !entry.done || !entry.d || entry.d.count !== 0) return;
      const k = routeKey(r);
      if (this.peeks[k]) return;
      const t2 = this.peeks[k] = { done: false, d: null };
      q.peek(r).then((d) => { t2.d = d; }, () => { delete this.peeks[k]; t2.failed = true; })
        .then(() => { t2.done = true; if (this.p && this.tier2Route(this.p) === r) this.render(); });
    }
    tier2State(p) {
      const r = this.tier2Route(p);
      if (!r) return null;
      const e = this.peeks[routeKey(r)];
      return e && e.done ? e : null;
    }

    parseText(text) {
      const q = Q();
      if (!q) return null;
      if (!this.parsed[text]) this.parsed[text] = q.parse(text, { prefixLast: false });
      return this.parsed[text];
    }

    /* ---------- drawing ---------- */

    render() {
      const term = this.term;
      if (!term) { this.showIdle(); return; }
      const phone = window.matchMedia(PHONE).matches;
      const p = this.p;
      const fresh = this.netKey === termKey(term) ? this.net : null;
      const net = fresh || this.staleNet(term);
      const peekOn = !!p && this.peekable(p);
      const pk = peekOn ? this.peekState(p) : null;
      const question = !!p && p.kind === 'question';
      // An exact search that names no piece: "Pieces That Mention ..." (tier 2), as on its page.
      const t2On = !!(pk && pk.d && pk.d.count === 0 && this.tier2Route(p));
      const t2 = t2On ? this.tier2State(p) : null;
      const seen = {};
      const health = !!p && p.kind === 'health';
      const hasProds = !!(pk && pk.d && pk.d.count > 0 && !question) || !!(t2 && t2.d && t2.d.count > 0);

      const best = this.bestGroup(p, pk, phone, seen);
      // On a computer the suggestions drop to 4 when products show, so the panel fits its height.
      const sug = health ? null : this.sugGroup(p, net, phone || hasProds ? 4 : 6, seen);
      let prod = health || question || !pk ? null : this.prodGroup(p, pk, phone ? 3 : 4, best);
      if (!prod && t2) prod = this.prodGroup(p, t2, phone ? 3 : 4, best, fill(COPY.t2, (p.terms || []).join(' ').replace(/_/g, ' ')));
      // A question's guides come from its own Guides view when Shopify suggests none.
      const guides = health ? null : this.guideGroup(p, net, 2, seen, question && pk && pk.d ? pk.d.items : null);

      const list = [];
      if (best) list.push(best);
      (question ? [guides, sug, prod] : [sug, prod, guides]).forEach(function (g) { if (g) list.push(g); });

      const settled = (!!fresh || letters(term) < 2) && (!peekOn || !!pk) && (!t2On || !!t2);
      if (!list.length) {
        // Never leave rows built from earlier letters on screen: while these words load, only their own
        // See All Results shows (the same place Enter goes). "Nothing matched" only once the destination is empty.
        if (!settled) { this.paint([this.footGroup(p, pk)]); return; }
        if (letters(term) >= 3) this.showNothing(term); else this.showIdle(true);
        return;
      }
      list.push(this.footGroup(p, pk));
      this.paint(list);
    }

    // While the new suggestions load, keep the last ones that still start with the typed words.
    staleNet(term) {
      if (!this.lastNet) return null;
      const t = norm(term);
      return {
        queries: this.lastNet.queries.filter(function (s) { return norm(s.text).indexOf(t) === 0; }),
        guides: this.lastNet.guides,
        stale: true
      };
    }

    optId(group, i) { return this.uid + '-' + group + '-' + i; }

    // Best Match (P0-7, P0-6 health line, P0-8 Help rows).
    bestGroup(p, pk, phone, seen) {
      const q = Q();
      if (!p || !q) return null;
      const cards = [], rows = [];
      let label = COPY.best, note = '', rowsLabel = '';
      const d = pk && pk.d;
      const live = d && d.count >= 0 ? d.count : null;
      // 2026-10-20 fix stage: "2 products" too (a 2-piece stone is a Shop All row now, not one piece's name).
      const countTxt = live != null && live >= 2 ? fill(COPY.count, live) : '';
      const a = p.anchor;
      const kind = p.kind;
      let productHandle = '';

      if (kind === 'health') {
        label = '';
        note = COPY.health;
        if (p.didYouMean && p.didYouMean.length) {
          rowsLabel = COPY.dym;
          const dm = p.didYouMean[0];
          rows.push({ title: dm.label, img: dm.img, href: dm.route, group: 'dym' });
        }
      } else if (kind === 'service') {
        cards.push({ title: p.label, tag: COPY.help, href: p.route, act: 'go' });
      } else if (kind === 'deals') {
        cards.push({ title: COPY.deals, href: p.route, act: 'go' });
      } else if (kind === 'product') {
        cards.push({ title: p.label, img: a && a.img, href: p.route, act: 'go' });
      } else if (kind === 'category') {
        this.categoryRows(p, phone, cards, rows);
      } else if (kind === 'question') {
        // The guide itself (Enter opens the Guides view with this guide pinned first).
        if (p.guide) cards.push({ title: p.guide.title, tag: COPY.guide, href: p.guide.url });
        if (a && a.route) cards.push({ title: COPY.shopAll + a.label, img: a.img, href: a.route });
      } else if (kind === 'nostock') {
        // The note leads only when the exact title search is still empty (it switches off when stock arrives).
        // Proposed notes wait for Paul's yes.
        if (d && d.count === 0 && p.nostock && !p.nostock.proposed) {
          label = '';
          note = p.nostock.msg;
          p.nostock.alts.forEach(function (h) {
            const c = q.collection(h);
            if (c && c.count > 0) rows.push({ title: c.label, img: c.img, href: c.url, group: 'note' });
          });
        }
      } else if (kind === 'unavailable' || (kind === 'exact' && !a)) {
        // Nothing claimed: no Best Match row; the products below are exact matches.
      } else {
        let title = SHOP_ALL.indexOf(kind) >= 0 ? COPY.shopAll + p.label : p.label;
        let img = a ? a.img : '';
        if (kind === 'all') { const c = q.collection('shop-all-crystals-stones'); if (c) img = c.img; }
        let href = p.route, sub = countTxt;
        if (kind === 'stone' && a) {
          // A stone with 1 piece: the row is the piece itself. (2 pieces showed one piece's name and price but
          // opened the collection; they now read "Shop All Angelite" with "2 products".)
          const small = live != null ? live === 1 : a.count === 1;
          if (small) {
            title = a.label;
            const it = d && d.items && d.items[0];
            if (it) {
              title = splitTitle(it.title).name || it.name || title;
              img = it.img || img;
              sub = priceText(it);
              if (pk.href) href = pk.href;
              productHandle = it.handle;
            }
          }
        }
        cards.push({ title: title, img: img, sub: sub, href: href, act: 'go' });
        // The kit that leads this landing ("beginner", "new home"): its own card under Best Match.
        if (p.kitLead && p.kit && p.kit.handle) {
          const k = this.kits[p.kit.handle];
          if (k && k.ok) cards.push({ title: k.title, img: k.img, sub: k.price, tag: COPY.kit, href: p.kit.url, group: 'kit' });
        }
      }

      // Did You Mean stones (colors, "amet" -> Ametrine, "tige" -> Tiger Iron): up to 2, steady from the 3rd letter.
      if (kind !== 'health' && p.didYouMean && p.didYouMean.length) {
        const used = cards.concat(rows).map(function (c) { return routeKey(c.href); });
        const dyms = p.didYouMean.filter(function (dm) { return used.indexOf(routeKey(dm.route)) < 0; }).slice(0, 2);
        if (!cards.length && !rows.length && dyms.length) label = COPY.dym;
        dyms.forEach(function (dm) { rows.push({ title: dm.label, img: dm.img, href: dm.route, group: 'dym' }); });
      }
      if (!cards.length && !rows.length && !note) return null;

      let html = '', pos = 0;
      const noteId = this.uid + '-note';
      const desc = note ? ' aria-describedby="' + noteId + '"' : '';
      if (label) html += '<div class="bb-pp__label" id="' + this.uid + '-l-best">' + esc(label) + '</div>';
      if (note) html += '<div class="bb-pp__note" id="' + noteId + '">' + esc(note) + '</div>';
      cards.forEach((c) => {
        pos++;
        seen[routeKey(c.href)] = 1;
        html += '<a id="' + this.optId('best', pos) + '" class="bb-pp__card bb-pp__opt" role="option" aria-selected="false"' + desc + ' href="' + esc(c.href) + '" data-bb-group="' + (c.group || 'best') + '" data-bb-pos="' + pos + '"' + (c.act ? ' data-bb-act="' + c.act + '"' : '') + '>' +
          (c.img ? '<span class="bb-pp__cimg"><img src="' + esc(sized(c.img, 160)) + '" alt="" width="64" height="64" loading="lazy"></span>' : '') +
          '<span class="bb-pp__ctxt"><span class="bb-pp__cname">' + esc(c.title) + (c.tag ? ' <span class="bb-pp__tag">' + esc(c.tag) + '</span>' : '') + '</span>' +
          (c.sub ? '<span class="bb-pp__csub">' + esc(c.sub) + '</span>' : '') + '</span>' +
          '<span class="bb-pp__arrow" aria-hidden="true">&rarr;</span></a>';
      });
      if (rows.length) {
        if (rowsLabel) html += '<div class="bb-pp__sublabel">' + esc(rowsLabel) + '</div>';
        html += '<div class="bb-pp__rows">';
        rows.forEach((r) => {
          pos++;
          seen[routeKey(r.href)] = 1;
          html += '<a id="' + this.optId('best', pos) + '" class="bb-pp__row bb-pp__opt" role="option" aria-selected="false"' + desc + ' href="' + esc(r.href) + '" data-bb-group="' + (r.group || 'best') + '" data-bb-pos="' + pos + '"' + (r.act ? ' data-bb-act="' + r.act + '"' : '') + '>' +
            '<span class="bb-pp__rimg' + (r.img ? '' : ' bb-pp__rimg--none') + '">' + (r.img ? '<img src="' + esc(sized(r.img, 96)) + '" alt="" width="36" height="36" loading="lazy">' : '') + '</span>' +
            '<span class="bb-pp__rname">' + esc(r.title) + '</span>' +
            (r.meta ? '<span class="bb-pp__meta">' + esc(r.meta) + '</span>' : '') + '</a>';
        });
        html += '</div>';
      }
      const kitShown = cards.filter(function (c) { return c.group === 'kit'; })[0];
      return { key: 'best', label: label || rowsLabel || COPY.best, html: html, productHandle: productHandle, kitHandle: kitShown && p.kit ? p.kit.handle : '', note: note };
    }

    // Bare "chakra": the Seven Chakra kit, chakra rows (4 on phone), then Crystals by Chakra.
    // Bare "zodiac": 4 signs, then Crystals by Zodiac. "7 chakra": the kit.
    categoryRows(p, phone, cards, rows) {
      const q = Q();
      let V = null;
      try { V = q.debug(); } catch (e) { V = null; }
      const a = p.anchor;
      const listOf = function (t, n, words) {
        if (!V) return;
        rank(V.entries.filter(function (e) { return e.t === t && e.handle; })).slice(0, n).forEach(function (e) {
          const c = q.collection(e.handle);
          const r = q.parse(words(e), { prefixLast: false });
          if (c && c.count > 0 && r.route) rows.push({ title: c.label, img: c.img, href: r.route });
        });
      };
      if (p.route === '/pages/crystals-by-chakra') {
        const kit = q.parse('7 chakra', { prefixLast: false });
        if (kit.anchor) cards.push({ title: kit.anchor.label, img: kit.anchor.img, href: kit.route });
        listOf('c', phone ? 4 : 7, function (e) { return e.label.toLowerCase() + ' chakra'; });
        rows.push({ title: p.label, href: p.route, act: 'go', meta: COPY.guide });
      } else if (p.route === '/pages/crystals-by-zodiac') {
        listOf('z', 4, function (e) { return e.label.toLowerCase(); });
        rows.push({ title: p.label, href: p.route, act: 'go', meta: COPY.guide });
      } else {
        cards.push({ title: p.label, img: a && a.img, href: p.route, act: 'go' });
      }
    }

    // Shopify's query suggestions (P0-7): typed letters plain, the completion bold. Dropped: repeats of
    // Best Match, health, not in stock, service, "welcome gift", and anything whose anchor has 0 pieces.
    sugGroup(p, net, max, seen) {
      if (!net || !net.queries || !net.queries.length) return null;
      const q = Q();
      const typedN = norm(this.term);
      const items = [];
      for (let i = 0; i < net.queries.length && items.length < max; i++) {
        const s = net.queries[i];
        const tn = norm(s.text);
        if (!tn || tn === typedN || /welcome gift/i.test(s.text)) continue;
        let href = s.url;
        if (q) {
          const sp = this.parseText(s.text);
          if (!sp || !sp.route || ['health', 'nostock', 'service', 'unavailable'].indexOf(sp.kind) >= 0) continue;
          if (sp.anchor && sp.anchor.count === 0) continue;
          const k = routeKey(sp.route);
          if (seen[k]) continue;
          seen[k] = 1;
          href = sp.route;
        }
        items.push({ text: s.text, parts: s.parts, href: href });
      }
      if (!items.length) return null;
      let html = '<div class="bb-pp__rows">';
      items.forEach((s, i) => {
        const pos = i + 1;
        const txt = s.parts && s.parts.length ? s.parts.map(function (x) { return x.typed ? '<span class="bb-pp__typed">' + esc(x.t) + '</span>' : '<b>' + esc(x.t) + '</b>'; }).join('') : esc(s.text);
        html += '<a id="' + this.optId('sug', pos) + '" class="bb-pp__sug bb-pp__opt" role="option" aria-selected="false" href="' + esc(s.href) + '" data-text="' + esc(s.text) + '" data-bb-group="suggestion" data-bb-pos="' + pos + '" data-bb-act="sug">' +
          SEARCH_ICON + '<span class="bb-pp__stext">' + txt + '</span></a>';
      });
      html += '</div>';
      return { key: 'sug', label: 'Suggestions', html: html };
    }

    // Products: the destination's own first pieces (3 on phone, 4 on a computer). Hidden when empty.
    prodGroup(p, pk, max, best, label) {
      const d = pk.d;
      if (!d || d.count <= 0 || !d.items || !d.items.length) return null;
      // Pieces already shown as a Best Match row (the 1-piece stone, the leading kit) are not repeated.
      const skip = best ? [best.productHandle, best.kitHandle] : [];
      const items = d.items.filter(function (it) { return skip.indexOf(it.handle) < 0 && (!it.type || it.type === 'product'); }).slice(0, max);
      if (!items.length) return null;
      label = label || COPY.products;
      let html = '<div class="bb-pp__label" id="' + this.uid + '-l-prod">' + esc(label) + '</div><div class="bb-pp__prods">';
      items.forEach((it, i) => {
        const pos = i + 1;
        const t = splitTitle(it.title || it.name);
        html += '<a id="' + this.optId('prod', pos) + '" class="bb-pp__prod bb-pp__opt" role="option" aria-selected="false" href="' + esc(it.url) + '" data-bb-group="product" data-bb-pos="' + pos + '">' +
          '<span class="bb-pp__pimg">' + (it.img ? '<img src="' + esc(sized(it.img, 360)) + '" srcset="' + esc(sized(it.img, 180)) + ' 180w, ' + esc(sized(it.img, 360)) + ' 360w, ' + esc(sized(it.img, 480)) + ' 480w" sizes="(min-width: 750px) 220px, 56px" alt="' + esc(t.name) + '" width="120" height="120" loading="lazy">' : '') + '</span>' +
          '<span class="bb-pp__ptxt"><span class="bb-pp__pname">' + esc(t.name || it.name) + '</span>' +
          (t.spec ? '<span class="bb-pp__spec">' + esc(t.spec) + '</span>' : '') +
          '<span class="bb-pp__price' + (it.available ? '' : ' bb-pp__price--sold') + '">' + esc(priceText(it)) + '</span></span></a>';
      });
      html += '</div>';
      return { key: 'prod', label: label, html: html };
    }

    // Guides and Articles: the pinned guide for the words (P0-8 pin map), then Shopify's pages and articles. 2 in all.
    guideGroup(p, net, max, seen, dest) {
      const list = [];
      const path = function (u) { return routeKey(String(u || '').split('?')[0]); };
      const push = function (g) {
        if (!g || !g.url || list.length >= max) return;
        const k = path(g.url);
        if (seen[k] || /welcome gift/i.test(g.title) || list.some(function (x) { return x.k === k; })) return;
        list.push({ k: k, title: g.title, url: g.url, kind: g.kind || 'page' });
      };
      if (p && p.guide && !(p.kind === 'question' && seen[path(p.guide.url)])) push({ title: p.guide.title, url: p.guide.url, kind: 'page' });
      if (net && net.guides) net.guides.forEach(push);
      // A question's own Guides view (the peek of its route), when Shopify's suggestions found none.
      if (dest) dest.forEach(function (it) { if (it.type === 'page' || it.type === 'article') push({ title: it.title, url: it.url, kind: it.type }); });
      if (!list.length) return null;
      let html = '<div class="bb-pp__label" id="' + this.uid + '-l-guides">' + esc(COPY.guides) + '</div><div class="bb-pp__rows">';
      list.forEach((g, i) => {
        const pos = i + 1;
        seen[g.k] = 1;
        html += '<a id="' + this.optId('guide', pos) + '" class="bb-pp__guide bb-pp__opt" role="option" aria-selected="false" href="' + esc(g.url) + '" data-bb-group="guide" data-bb-pos="' + pos + '">' +
          '<span class="bb-pp__gname">' + esc(g.title) + '</span><span class="bb-pp__meta">' + esc(g.kind === 'article' ? COPY.journal : COPY.guide) + '</span></a>';
      });
      html += '</div>';
      return { key: 'guides', label: COPY.guides, html: html };
    }

    // "See All Results": the same destination as Enter, the one-piece product shortcut included.
    footGroup(p, pk) {
      const q = Q();
      let href = '';
      let full = p;
      if (!full && q) { try { full = q.parse(this.typed, { prefixLast: true }); } catch (e) { full = null; } }
      if (pk && pk.href) href = pk.href;
      else if (full && full.route) href = full.route;
      else href = (window.routes && window.routes.search_url || '/search') + '?q=' + encodeURIComponent(this.term) + '&options%5Bprefix%5D=last';
      const html = '<a id="' + this.optId('all', 1) + '" class="bb-pp__all bb-pp__opt ' + esc(this.buttonClass) + '" role="option" aria-selected="false" href="' + esc(href) + '" data-bb-group="all" data-bb-pos="1" data-bb-act="go">' + esc(COPY.all) + '</a>';
      return { key: 'all', label: COPY.all, html: html, foot: true };
    }

    ensureRoot() {
      if (this.root && this.root.parentNode === this.predictiveSearchResults) return;
      this.predictiveSearchResults.innerHTML = '';
      this.nothingEl = document.createElement('div');
      this.nothingEl.className = 'bb-pp bb-pp--nothing';
      this.nothingEl.hidden = true;
      this.root = document.createElement('div');
      this.root.className = 'bb-pp';
      this.root.id = 'predictive-search-results-list';
      this.root.setAttribute('role', 'listbox');
      this.root.setAttribute('aria-label', COPY.best);
      this.predictiveSearchResults.appendChild(this.nothingEl);
      this.predictiveSearchResults.appendChild(this.root);
      this.groupEls = {};
    }

    // Replace only the groups whose markup changed, so photos do not flicker while typing.
    paint(list) {
      this.ensureRoot();
      const keys = list.map(function (g) { return g.key; });
      Object.keys(this.groupEls).forEach((k) => {
        if (keys.indexOf(k) < 0) { this.groupEls[k].remove(); delete this.groupEls[k]; }
      });
      let prev = null;
      list.forEach((g) => {
        let el = this.groupEls[g.key];
        if (!el) {
          el = document.createElement('div');
          el.className = 'bb-pp__group bb-pp__group--' + g.key + (g.foot ? ' bb-pp__foot' : '');
          el.setAttribute('role', 'group');
          this.groupEls[g.key] = el;
        }
        el.setAttribute('aria-label', g.label);
        if (el.__bbHtml !== g.html) { el.innerHTML = g.html; el.__bbHtml = g.html; }
        const next = prev ? prev.nextSibling : this.root.firstChild;
        if (next !== el) this.root.insertBefore(el, next);
        prev = el;
      });
      this.nothingEl.hidden = true;
      if (this.activeId) {
        const a = document.getElementById(this.activeId);
        if (a && this.root.contains(a)) a.setAttribute('aria-selected', 'true');
        else this.setActive(null);
      }
      this.predictiveSearchResults.classList.remove('visually-hidden');
      this.setAttribute('results', true);
      if (this.modal) { this.modal.classList.add('searching'); this.modal.classList.remove('bb-pp-nothing'); }
      this.showClear();
      this.open();
      const n = this.options().length - 1;
      // The health line is announced itself (screen readers never reach it by arrowing: it is not an option).
      const noted = list.filter(function (g) { return g.note; })[0];
      this.setLiveRegionText(noted ? noted.note : (n === 1 ? COPY.result1 : n > 0 ? fill(COPY.results, n) : COPY.none));
    }

    // Nothing at all for 3+ letters: the old message, then the "Popular Ways To Shop" photos.
    showNothing(term) {
      this.ensureRoot();
      Object.keys(this.groupEls).forEach((k) => { this.groupEls[k].remove(); });
      this.groupEls = {};
      this.setActive(null);
      this.nothingEl.innerHTML = '<div class="bb-pp__nothing">' + esc(fill(COPY.nothing, term)) + '</div>';
      this.nothingEl.hidden = false;
      this.predictiveSearchResults.classList.remove('visually-hidden');
      this.setAttribute('results', true);
      if (this.modal) { this.modal.classList.remove('searching'); this.modal.classList.add('bb-pp-nothing'); }
      this.showClear();
      this.open();
      this.input.setAttribute('aria-expanded', 'false'); // no options in the list
      this.setLiveRegionText(COPY.none);
    }

    // No words (or too few for anything): back to the "Popular Ways To Shop" photos.
    showIdle(keepOpen) {
      clearTimeout(this.timer);
      if (this.root) { Object.keys(this.groupEls).forEach((k) => { this.groupEls[k].remove(); }); this.groupEls = {}; }
      if (this.nothingEl) this.nothingEl.hidden = true;
      this.setActive(null);
      this.removeAttribute('results');
      this.predictiveSearchResults.classList.add('visually-hidden');
      if (this.modal) this.modal.classList.remove('searching', 'bb-pp-nothing');
      if (!this.term) this.setLoading(false);
      if (!keepOpen) this.removeAttribute('open');
      this.input.setAttribute('aria-expanded', 'false'); // the list is hidden (Clear, too few letters)
    }

    hasResults() { return !!(this.root && this.root.querySelector('[role="option"]')); }

    /* ---------- keyboard, clicks, Enter ---------- */

    options() {
      if (!this.root) return [];
      return Array.prototype.slice.call(this.root.querySelectorAll('[role="option"]'));
    }
    activeEl() {
      if (!this.activeId) return null;
      const a = document.getElementById(this.activeId);
      return a && this.root && this.root.contains(a) ? a : null;
    }
    setActive(el) {
      const prev = this.activeEl();
      if (prev) prev.setAttribute('aria-selected', 'false');
      if (!el) {
        this.activeId = '';
        this.input.setAttribute('aria-activedescendant', '');
        return;
      }
      el.setAttribute('aria-selected', 'true');
      this.activeId = el.id;
      this.input.setAttribute('aria-activedescendant', el.id);
      try { el.scrollIntoView({ block: 'nearest' }); } catch (e) { /* old browsers */ }
      // Copy a highlighted suggestion into the field; any other row shows the typed words.
      this.input.value = el.getAttribute('data-bb-act') === 'sug' ? el.getAttribute('data-text') : this.typed;
      this.setLiveRegionText(el.textContent.replace(/\s+/g, ' ').trim());
    }

    onKeydown(event) {
      if (event.isComposing) return;
      const k = event.key;
      if (k === 'ArrowDown' || k === 'ArrowUp') {
        event.preventDefault(); // keep the caret still
        this.move(k === 'ArrowDown' ? 1 : -1);
      } else if (k === 'Enter') {
        const a = this.activeEl();
        if (a) { event.preventDefault(); this.activate(a); }
      } else if (k === 'Escape') {
        // A search field empties itself on Escape in Chrome and Safari; the spec keeps the words.
        event.preventDefault();
        if (this.activeEl()) this.input.value = this.typed;
        this.setActive(null);
      }
    }

    // One flat list of every option; Up from the first row returns to the field.
    move(dir) {
      const opts = this.options();
      if (!opts.length || !this.hasAttribute('results')) return;
      const cur = this.activeEl();
      let i = cur ? opts.indexOf(cur) : -1;
      if (dir > 0) {
        i = Math.min(i + 1, opts.length - 1);
      } else {
        if (i <= 0) {
          if (cur) {
            this.setActive(null);
            this.input.value = this.typed;
            const n = this.input.value.length;
            try { this.input.setSelectionRange(n, n); } catch (e) { /* type=search may refuse */ }
          }
          return;
        }
        i -= 1;
      }
      this.setActive(opts[i]);
    }

    onResultsClick(event) {
      const el = event.target.closest('[role="option"]');
      if (!el || !this.root || !this.root.contains(el)) return;
      const q = Q();
      if (event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        // New tab or window: the link opens as it is.
        if (q) q.mark(this.typed.trim(), el.getAttribute('data-bb-act') === 'sug' ? 'suggestion' : 'popup');
        return;
      }
      event.preventDefault();
      this.activate(el);
    }

    activate(el) {
      const q = Q();
      const act = el.getAttribute('data-bb-act');
      const href = el.getAttribute('href');
      if (act === 'sug') {
        // Choosing a suggestion fills the field and goes where that suggestion's Best Match would.
        const text = el.getAttribute('data-text') || '';
        this.input.value = text;
        this.typed = text;
        if (q) {
          const sp = q.parse(text, { prefixLast: false });
          if (q.go(sp, 'suggestion')) return;
        }
        window.location.assign(href);
        return;
      }
      if (act === 'go' && q) {
        const p = this.p || q.parse(this.typed, { prefixLast: true });
        if (p && p.route && (href === p.route || el.getAttribute('data-bb-group') === 'all')) {
          if (q.go(p, 'popup')) return;
        }
      }
      if (q) q.mark(this.typed.trim(), 'popup');
      window.location.assign(href);
    }

    // Enter or the phone's Search key: the same destination as the Best Match row (P0-3).
    onFormSubmit(event) {
      const typed = this.input.value;
      if (!typed.trim()) { event.preventDefault(); return; }
      const a = this.activeEl();
      if (a) { event.preventDefault(); this.activate(a); return; }
      const q = Q();
      if (!q) return; // the word list did not load: plain /search submit
      let ok = false;
      try { ok = q.go(q.parse(typed, { prefixLast: true }), 'enter'); } catch (e) { ok = false; }
      if (ok) event.preventDefault();
    }

    /* ---------- theme plumbing ---------- */

    setLoading(on) {
      if (this.loadingSpinner) this.loadingSpinner.classList.toggle('hidden', !on);
      if (on) this.setAttribute('loading', true); else this.removeAttribute('loading');
      if (this.buttonText) this.buttonText.classList.toggle('hidden', on || !this.term);
    }

    // The Clear label shows whenever there are words and nothing is loading.
    showClear() {
      if (this.buttonText && !this.hasAttribute('loading')) this.buttonText.classList.toggle('hidden', !this.term);
    }

    setLiveRegionText(statusText) {
      if (!this.statusElement) return;
      this.statusElement.setAttribute('aria-hidden', 'false');
      this.statusElement.textContent = statusText;
      clearTimeout(this.liveTimer);
      this.liveTimer = setTimeout(() => { this.statusElement.setAttribute('aria-hidden', 'true'); }, 1000);
    }

    open() {
      this.setAttribute('open', true);
      this.input.setAttribute('aria-expanded', true);
      document.body.classList.add('predictive-search--focus');
    }

    // Closing keeps the words (Escape, the close button, Cancel, the overlay); only Clear empties the field.
    close(clearSearchTerm) {
      if (this.activeEl()) this.input.value = this.typed;
      this.setActive(null);
      this.removeAttribute('open');
      this.input.setAttribute('aria-expanded', false);
      document.body.classList.remove('predictive-search--focus');
      if (clearSearchTerm === true) this.clear();
    }

    clear(event) {
      if (event && event.preventDefault) event.preventDefault();
      if (this.abortController) this.abortController.abort();
      this.input.value = '';
      this.typed = '';
      this.term = '';
      this.p = null;
      this.showIdle();
      this.setLoading(false);
      if (this.buttonText) this.buttonText.classList.add('hidden');
      this.input.focus();
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
})();
